<template>
  <div class="payment-container">
    <div class="payment-header">
      <h2>支付订单</h2>
      <div class="payment-summary">
        <span class="amount">¥{{ amount }}</span>
        <span class="currency">人民币</span>
      </div>
      <div class="order-info" v-if="orderNumber">
        <p>订单号: {{ orderNumber }}</p>
      </div>
    </div>

    <!-- 支付方式选择 -->
    <div class="payment-methods" v-if="!selectedMethod">
      <h3>选择支付方式</h3>
      
      <!-- 信用卡支付 -->
      <div class="payment-method-card" @click="selectMethod('stripe')">
        <div class="method-icon">
          <i class="fas fa-credit-card"></i>
        </div>
        <div class="method-info">
          <h4>信用卡/借记卡</h4>
          <p>Visa, MasterCard, American Express</p>
        </div>
        <div class="method-arrow">
          <i class="fas fa-chevron-right"></i>
        </div>
      </div>

      <!-- PayPal -->
      <div class="payment-method-card" @click="selectMethod('paypal')">
        <div class="method-icon paypal">
          <i class="fab fa-paypal"></i>
        </div>
        <div class="method-info">
          <h4>PayPal</h4>
          <p>使用PayPal账户快速支付</p>
        </div>
        <div class="method-arrow">
          <i class="fas fa-chevron-right"></i>
        </div>
      </div>

      <!-- 支付宝 -->
      <div class="payment-method-card" @click="selectMethod('alipay')">
        <div class="method-icon alipay">
          <i class="fas fa-mobile-alt"></i>
        </div>
        <div class="method-info">
          <h4>支付宝</h4>
          <p>扫码支付或账户支付</p>
        </div>
        <div class="method-arrow">
          <i class="fas fa-chevron-right"></i>
        </div>
      </div>

      <!-- 微信支付 -->
      <div class="payment-method-card" @click="selectMethod('wechat')">
        <div class="method-icon wechat">
          <i class="fab fa-weixin"></i>
        </div>
        <div class="method-info">
          <h4>微信支付</h4>
          <p>使用微信扫码支付</p>
        </div>
        <div class="method-arrow">
          <i class="fas fa-chevron-right"></i>
        </div>
      </div>
    </div>

    <!-- Stripe 信用卡支付表单 -->
    <div v-if="selectedMethod === 'stripe'" class="stripe-payment-form">
      <div class="form-header">
        <button @click="backToMethods" class="back-button">
          <i class="fas fa-arrow-left"></i> 返回
        </button>
        <h3>信用卡支付</h3>
      </div>

      <!-- 步骤1: 创建订单和Payment Intent -->
      <div v-if="!clientSecret" class="step-container">
        <div class="step-indicator">
          <div class="step active">1</div>
          <div class="step-line"></div>
          <div class="step">2</div>
          <div class="step-line"></div>
          <div class="step">3</div>
        </div>
        <div class="step-content">
          <h4>正在创建支付订单...</h4>
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <p>请稍候，正在为您生成支付订单</p>
        </div>
      </div>

      <!-- 步骤2: 支付表单 -->
      <div v-else-if="!processing" class="step-container">
        <div class="step-indicator">
          <div class="step completed">1</div>
          <div class="step-line completed"></div>
          <div class="step active">2</div>
          <div class="step-line"></div>
          <div class="step">3</div>
        </div>
        <div class="step-content">
          <h4>输入支付信息</h4>
          <div id="card-element" class="card-element">
            <!-- Stripe Elements 将在这里渲染 -->
          </div>
          <div id="card-errors" class="card-errors" v-if="cardErrors">
            <i class="fas fa-exclamation-triangle"></i>
            {{ cardErrors }}
          </div>
          <button 
            @click="confirmPayment" 
            :disabled="!canConfirmPayment"
            class="confirm-button"
          >
            确认支付 ¥{{ amount }}
          </button>
        </div>
      </div>

      <!-- 步骤3: 处理支付 -->
      <div v-else class="step-container">
        <div class="step-indicator">
          <div class="step completed">1</div>
          <div class="step-line completed"></div>
          <div class="step completed">2</div>
          <div class="step-line completed"></div>
          <div class="step active">3</div>
        </div>
        <div class="step-content">
          <h4>正在处理支付...</h4>
          <div class="loading-spinner">
            <i class="fas fa-spinner fa-spin"></i>
          </div>
          <p>请勿关闭页面，正在确认您的支付</p>
        </div>
      </div>
    </div>

    <!-- 其他支付方式 -->
    <div v-else-if="selectedMethod" class="other-payment-form">
      <div class="form-header">
        <button @click="backToMethods" class="back-button">
          <i class="fas fa-arrow-left"></i> 返回
        </button>
        <h3>{{ getPaymentMethodName() }}</h3>
      </div>

      <!-- PayPal 支付 -->
      <div v-if="selectedMethod === 'paypal'" class="paypal-form">
        <div id="paypal-button-container">
          <!-- PayPal 按钮将在这里渲染 -->
        </div>
      </div>

      <!-- 支付宝支付 -->
      <div v-if="selectedMethod === 'alipay'" class="alipay-form">
        <div class="qr-code-container">
          <div class="qr-code" v-if="alipayQRCode">
            <img :src="alipayQRCode" alt="支付宝二维码" />
            <p>请使用支付宝扫描二维码支付</p>
          </div>
          <div v-else class="loading">
            <i class="fas fa-spinner fa-spin"></i>
            <p>正在生成支付二维码...</p>
          </div>
        </div>
      </div>

      <!-- 微信支付 -->
      <div v-if="selectedMethod === 'wechat'" class="wechat-form">
        <div class="qr-code-container">
          <div class="qr-code" v-if="wechatQRCode">
            <img :src="wechatQRCode" alt="微信支付二维码" />
            <p>请使用微信扫描二维码支付</p>
          </div>
          <div v-else class="loading">
            <i class="fas fa-spinner fa-spin"></i>
            <p>正在生成支付二维码...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 错误和成功消息 -->
    <div v-if="errorMessage" class="error-message">
      <i class="fas fa-exclamation-triangle"></i>
      {{ errorMessage }}
    </div>

    <div v-if="successMessage" class="success-message">
      <i class="fas fa-check-circle"></i>
      {{ successMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'StripePayment',
  props: {
    amount: {
      type: Number,
      required: true,
      default: 100
    },
    orderId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedMethod: '',
      processing: false,
      errorMessage: '',
      successMessage: '',
      cardErrors: '',
      clientSecret: '',
      orderNumber: this.orderId || this.generateOrderNumber(),
      stripe: null,
      elements: null,
      cardElement: null,
      alipayQRCode: '',
      wechatQRCode: ''
    };
  },
  computed: {
    canConfirmPayment() {
      return this.clientSecret && this.stripe && this.cardElement && !this.cardErrors;
    }
  },
  async mounted() {
    await this.initStripe();
  },
  methods: {
    selectMethod(method) {
      this.selectedMethod = method;
      this.errorMessage = '';
      this.successMessage = '';
      
      if (method === 'stripe') {
        this.createPaymentIntent();
      } else if (method === 'alipay' || method === 'wechat') {
        this.generateQRCode(method);
      }
    },
    
    backToMethods() {
      this.selectedMethod = '';
      this.clientSecret = '';
      this.cardErrors = '';
      this.errorMessage = '';
      this.successMessage = '';
    },
    
    generateOrderNumber() {
      return 'ORD-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    },
    
    getPaymentMethodName() {
      const names = {
        paypal: 'PayPal',
        alipay: '支付宝',
        wechat: '微信支付'
      };
      return names[this.selectedMethod] || '支付';
    },
    
    async initStripe() {
      try {
        // 动态导入 Stripe
        const { loadStripe } = await import('@stripe/stripe-js');
        
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        
        if (!this.stripe) {
          throw new Error('Stripe 初始化失败');
        }
      } catch (error) {
        console.error('Stripe初始化失败:', error);
        this.errorMessage = '支付系统初始化失败，请刷新页面重试';
      }
    },
    
    // 步骤1: 创建订单和Payment Intent
    async createPaymentIntent() {
      try {
        // 调用后端API创建订单和Payment Intent
        const response = await this.$http.post('/api/payment/create-intent', {
          amount: this.amount * 100, // 转换为分
          currency: 'cny',
          orderNumber: this.orderNumber,
          description: `订单 ${this.orderNumber} 支付`
        });
        
        if (response.data.success) {
          this.clientSecret = response.data.clientSecret;
          this.orderNumber = response.data.orderNumber;
          
          // 初始化Stripe Elements
          await this.initStripeElements();
        } else {
          throw new Error(response.data.message || '创建支付订单失败');
        }
      } catch (error) {
        console.error('创建Payment Intent失败:', error);
        this.errorMessage = error.response?.data?.message || error.message || '创建支付订单失败，请重试';
      }
    },
    
    // 初始化Stripe Elements
    async initStripeElements() {
      try {
        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret
        });
        
        this.cardElement = this.elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#424770',
              '::placeholder': {
                color: '#aab7c4',
              },
            },
          },
        });
        
        this.cardElement.mount('#card-element');
        this.cardElement.on('change', this.handleCardChange);
      } catch (error) {
        console.error('初始化Stripe Elements失败:', error);
        this.errorMessage = '支付表单初始化失败，请重试';
      }
    },
    
    handleCardChange(event) {
      this.cardErrors = event.error ? event.error.message : '';
    },
    
    // 步骤3: 确认支付
    async confirmPayment() {
      this.processing = true;
      this.errorMessage = '';
      
      try {
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(this.clientSecret, {
          payment_method: {
            card: this.cardElement,
            billing_details: {
              name: 'Customer Name', // 可以从表单获取
            },
          }
        });
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (paymentIntent.status === 'succeeded') {
          this.successMessage = '支付成功！';
          this.emitPaymentSuccess('stripe', paymentIntent);
        } else {
          throw new Error('支付失败，请重试');
        }
      } catch (error) {
        console.error('支付确认失败:', error);
        this.errorMessage = error.message || '支付失败，请重试';
      } finally {
        this.processing = false;
      }
    },
    
    async generateQRCode(method) {
      try {
        // 调用后端API生成二维码
        const response = await this.$http.post('/api/payment/create-qr', {
          method: method,
          amount: this.amount,
          orderNumber: this.orderNumber
        });
        
        if (response.data.success) {
          if (method === 'alipay') {
            this.alipayQRCode = response.data.qrCode;
          } else if (method === 'wechat') {
            this.wechatQRCode = response.data.qrCode;
          }
        } else {
          throw new Error(response.data.message || '生成二维码失败');
        }
      } catch (error) {
        console.error('生成二维码失败:', error);
        this.errorMessage = error.message || '生成支付二维码失败，请重试';
      }
    },
    
    emitPaymentSuccess(method, paymentIntent = null) {
      this.$emit('payment-success', {
        amount: this.amount,
        method: method,
        orderNumber: this.orderNumber,
        paymentIntent: paymentIntent
      });
    }
  }
};
</script>

<style scoped>
.payment-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.payment-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.payment-header h2 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 24px;
}

.payment-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
}

.amount {
  font-size: 28px;
  font-weight: bold;
  color: #007bff;
}

.currency {
  color: #666;
  font-size: 14px;
}

.order-info p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.payment-methods h3 {
  margin-bottom: 20px;
  color: #333;
  font-size: 18px;
}

.payment-method-card {
  display: flex;
  align-items: center;
  padding: 15px;
  margin-bottom: 10px;
  border: 2px solid #eee;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.payment-method-card:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}

.method-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
  color: white;
  background-color: #007bff;
}

.method-icon.paypal { background-color: #003087; }
.method-icon.alipay { background-color: #1677ff; }
.method-icon.wechat { background-color: #07c160; }

.method-info {
  flex: 1;
}

.method-info h4 {
  margin: 0 0 5px 0;
  font-size: 16px;
  color: #333;
}

.method-info p {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.method-arrow {
  color: #ccc;
  font-size: 16px;
}

.form-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.back-button {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 5px 10px;
  margin-right: 15px;
  font-size: 14px;
}

.back-button:hover {
  color: #0056b3;
}

.form-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.step-container {
  text-align: center;
}

.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
}

.step {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.step.active {
  background-color: #007bff;
  color: white;
}

.step.completed {
  background-color: #28a745;
  color: white;
}

.step-line {
  width: 60px;
  height: 2px;
  background-color: #e9ecef;
  margin: 0 10px;
}

.step-line.completed {
  background-color: #28a745;
}

.step-content h4 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 18px;
}

.loading-spinner {
  margin: 20px 0;
}

.loading-spinner i {
  font-size: 32px;
  color: #007bff;
}

.step-content p {
  color: #666;
  margin: 0;
}

.card-element {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 20px;
  background-color: #f8f9fa;
}

.card-errors {
  color: #dc3545;
  font-size: 14px;
  margin-bottom: 20px;
  padding: 10px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.confirm-button {
  width: 100%;
  background-color: #007bff;
  color: white;
  padding: 15px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.confirm-button:hover:not(:disabled) {
  background-color: #0056b3;
}

.confirm-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.qr-code-container {
  text-align: center;
  padding: 30px;
}

.qr-code img {
  max-width: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin-bottom: 15px;
}

.loading {
  color: #666;
}

.loading i {
  font-size: 24px;
  margin-bottom: 10px;
}

.error-message, .success-message {
  margin-top: 20px;
  padding: 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.success-message {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

@media (max-width: 768px) {
  .payment-container {
    margin: 10px;
    padding: 15px;
  }
  
  .step-line {
    width: 40px;
  }
}
</style>